import { CSSProperties, FC, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AnimatedArrow from 'src/components/common/AnimatedArrow';
import ItemButton from 'src/components/common/ItemButton';
import { naturalSort } from 'src/functions/functions';
import { getHtmlTags } from 'src/functions/getHtmlTags';
import useResponsive from 'src/hooks/contexts/useResponsive';
import { activateSwipe, deactivateSwipe } from 'src/hooks/events/useNavigation/useNavigation.functions';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import Spacing from 'src/styles/layouts/Spacing';
import NGSkeleton from 'src/styles/theme/components/NGSkeleton';
import NGText from 'src/styles/theme/components/NGText';
import { EFontWeight } from 'src/styles/theme/components/NGText/NGText.enum';
import { ESize, ETextColor } from 'src/styles/theme/utils/enums';
import { ETableSortDirection } from './NGTable.enum';
import { StyledCell, StyledRow, StyledTable, StyledTableContainer, StyledThead } from './NGTable.styles';
import { INGTable, ITableHeader } from './NGTable.type';

const inlineCellStyle: CSSProperties = { opacity: `0`, position: 'absolute' };
const rowBuffer = 20;

const NGTable: FC<INGTable> = forwardRef(
	(
		{
			headers,
			getId = (rowData: any) => rowData.id,
			rowCy,
			onClick,
			onMouseEnter,
			onMouseLeave,
			selectedId,
			checkHtmlTags,
			tableLayout = 'auto',
			data,
			isLoading,
			error,
			headerHidden,
			isARowSelected,
			headerAlwaysVisible = false,
			deactivateSwipOnTouch = false,
			forceSelectedRows = false,
			noResultsLabel = 'No results',
			children,
			...otherProps
		},
		ref
	) => {
		const { isSmallerThanMd } = useResponsive();
		const formattedHeaders = useMemo<ITableHeader[]>(() => headers.filter((header) => !header.isHidden), [headers]);
		const [sortBy, setSortBy] = useState<ITableHeader>();
		const [sortDirection, setSortDirection] = useState<ETableSortDirection>(ETableSortDirection.ascending);

		const tableContainerRef = useRef<HTMLDivElement>(null);
		const rowRef = useRef<HTMLTableRowElement>(null);

		useEffect(() => {
			const row = rowRef.current;
			const table = tableContainerRef.current;

			if (isSmallerThanMd || !row || !table) {
				return;
			}

			const rowRect = row.getBoundingClientRect();
			const tableRect = table.getBoundingClientRect();
			const { top: rowTop, bottom: rowBottom } = rowRect;
			const { top: tableTop, bottom: tableBottom } = tableRect;
			const isInView = rowTop > tableTop + rowBuffer && rowBottom < tableBottom + rowBuffer;
			if (!isInView) {
				row.scrollIntoView({ block: 'center', behavior: 'smooth' });
			}
		}, [selectedId, isSmallerThanMd]);

		const handleSortClick = useCallback(
			(header: ITableHeader) => {
				if (!header.isSortable) {
					return;
				}

				if (sortBy !== header) {
					setSortBy(header);
					setSortDirection(ETableSortDirection.ascending);
					return;
				}

				if (sortDirection === ETableSortDirection.ascending) {
					setSortDirection(ETableSortDirection.descending);
					return;
				}

				setSortDirection(ETableSortDirection.ascending);
			},
			[sortBy, sortDirection]
		);

		const sortedRowData = useMemo(() => {
			if (!sortBy || !sortBy?.getValue) {
				return data;
			}

			return [...data].sort((a, b) => {
				let firstItem = a;
				let secondItem = b;

				if (sortDirection === ETableSortDirection.descending) {
					firstItem = b;
					secondItem = a;
				}

				// sort a and b by the header's label
				const firstItemValue = firstItem[sortBy.label];
				const secondItemValue = secondItem[sortBy.label];

				if (typeof firstItemValue === 'string') {
					return firstItemValue.localeCompare(secondItemValue);
				}

				if (sortBy && sortBy.getValue) {
					return naturalSort(sortBy.getValue(firstItem), sortBy.getValue(secondItem));
				}

				return data;
			});
		}, [data, sortBy, sortDirection]);

		const displayedRowData = useMemo(() => {
			return isLoading
				? [...Array(10)].map((_, index) => ({
						id: 'row-loading' + index,
						isLoading: true
				  }))
				: sortedRowData;
		}, [sortedRowData, isLoading]);

		return (
			<StyledTableContainer
				ref={tableContainerRef}
				width={'100%'}
				maxHeight={'100%'}
				onTouchStart={deactivateSwipOnTouch ? () => deactivateSwipe() : undefined}
				onTouchEnd={deactivateSwipOnTouch ? () => activateSwipe() : undefined}
				{...otherProps}
			>
				<StyledTable ref={ref} tableLayout={tableLayout}>
					<StyledThead isHidden={headerHidden}>
						<tr>
							{formattedHeaders.map((header) => (
								<StyledCell
									key={header.label}
									style={(isSmallerThanMd && !headerAlwaysVisible) || headerHidden ? inlineCellStyle : header.cellStyle}
								>
									<Flex vertical={EFlex.center}>
										<NGText size={ESize.m} fontWeight={EFontWeight.bold} textColor={ETextColor.gold}>
											{checkHtmlTags ? getHtmlTags(header.label) : header.label}
										</NGText>

										{header.isSortable && (
											<>
												<Spacing size={ESize['4xs']} />
												<AnimatedArrow
													isActive={sortBy?.label === header?.label && sortDirection === ETableSortDirection.ascending}
													width={16}
													height={11.3}
												/>
												<ItemButton onClick={() => handleSortClick(header)} />
											</>
										)}
									</Flex>
								</StyledCell>
							))}
						</tr>
					</StyledThead>
					<tbody>
						{displayedRowData?.length ? (
							<>
								{displayedRowData.map((row: any) => {
									const id = getId(row);

									// data-cy used for testing
									let _rowCy;

									if (row.isLoading) {
										return (
											<StyledRow key={row.id}>
												{formattedHeaders?.map((header) => (
													<StyledCell key={header.label + id}>
														<NGSkeleton width='100%' height={30} />
													</StyledCell>
												))}
											</StyledRow>
										);
									}

									if (rowCy) {
										_rowCy = rowCy(row);
									}

									return (
										<StyledRow
											key={id}
											ref={id === selectedId ? rowRef : undefined}
											data-cy-row={_rowCy}
											isRowClickable={onClick}
											isARowSelected={isARowSelected}
											isSelected={id === selectedId}
											forceSelectedRows={forceSelectedRows}
											onClick={onClick ? () => onClick(row) : undefined}
											onMouseEnter={onMouseEnter ? () => onMouseEnter(row) : undefined}
											onMouseLeave={onMouseLeave ? () => onMouseLeave(row) : undefined}
										>
											{formattedHeaders.map(({ label, cellStyle, getValue, render }) => {
												let value;
												try {
													value = getValue && typeof getValue === 'function' ? getValue(row) : undefined;
												} catch (e) {
													value = undefined;
												}

												return (
													<StyledCell key={`cell-${id}-${label}-${row.id}`} style={cellStyle}>
														{render ? (
															render(row, value)
														) : (
															<NGText size={ESize.m}>{checkHtmlTags ? getHtmlTags(value || '-') : value || '-'}</NGText>
														)}
													</StyledCell>
												);
											})}
										</StyledRow>
									);
								})}

								{children}
							</>
						) : (
							<>
								{children}

								<StyledRow>
									<StyledCell>
										<NGText size={ESize.s}>{noResultsLabel}</NGText>
									</StyledCell>
								</StyledRow>
							</>
						)}
					</tbody>
				</StyledTable>
			</StyledTableContainer>
		);
	}
);

// necessary with forwardRef
NGTable.displayName = 'NGTable';

export { NGTable };
